import {
  AssessmentsGrade,
  GET_ASSESSMENTS_AVERAGE_QUERY,
  GetMarkbookSort,
  GetMarkbookSortBy,
  getSortParams,
  removeObjectEmptyArrayValues,
  removeObjectUndefinedNullValues,
  SORT_DIRECTION,
  UseGetMarkbookQueriesParams,
} from '@schooly/api';
import {
  Assessment,
  AssessmentMethodType,
  FilterKeys,
  GET_MARKBOOK_QUERY,
  GetMarkbookParams,
  MarkbookArrangeBy,
  useGetAssessmentsGradesForSchoolQuery,
  useGetAssessmentsQuery,
  useGetMarkbookQuery,
} from '@schooly/api';
import { SavedFilter } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useMemo } from 'react';

import { MarkbookFilterPreview } from '../../../../context/markbook/MarkbookFilterContext';
import { DEFAULT_ASSESSMENTS_SORT } from '../../AssessmentsContent';
import { useAssessmentsAverage } from '../common/useAssessmentsAverage';
import { MarkbookViewSettings } from '../MarkbookSettings/MarkbookSettings';

export const MARKBOOK_PAGE_SIZE = 30;
const INITIAL_PAGE_NUMBER = 1;

const DEFAULT_MARKBOOK_SORT: GetMarkbookSort[] = [
  { sortBy: GetMarkbookSortBy.LastName, direction: SORT_DIRECTION.ASC },
  { sortBy: GetMarkbookSortBy.GivenName, direction: SORT_DIRECTION.ASC },
];

export type HandleChangeSortParams = {
  sortBy: GetMarkbookSortBy;
  assessmentId?: string;
  subjectId?: string;
};

type UseAssessmentsMarkbookForGroupsProps = {
  filterPreview: MarkbookFilterPreview | null;
  filter?: SavedFilter;
  settings: MarkbookViewSettings;
};

export const useAssessmentsMarkbookForGroups = ({
  filterPreview,
  filter,
  settings,
}: UseAssessmentsMarkbookForGroupsProps) => {
  const { schoolId } = useAuth();
  const queryClient = useQueryClient();

  const [isSorting, setIsSorting] = useState(false);
  const [markbookQueriesParams, setMarkbookQueriesParams] = useState<UseGetMarkbookQueriesParams[]>(
    [],
  );

  const pageFilters = filterPreview?.filter ?? filter?.filter;

  const hasSelectedAssessment = Boolean(pageFilters?.[FilterKeys.Assessment]?.length);
  const enableAssessmentsQuery = Boolean(schoolId && hasSelectedAssessment);

  const {
    data: assessmentsData,
    isLoading: isAssessmentsLoading,
    setParams: setAssessmentsParams,
  } = useGetAssessmentsQuery(
    {
      school_id: schoolId || '',
      sort: DEFAULT_ASSESSMENTS_SORT,
      filters: { [FilterKeys.Assessment]: pageFilters?.[FilterKeys.Assessment] },
    },
    {
      refetchOnMount: 'always',
      enabled: enableAssessmentsQuery,
    },
  );

  const { assessments, assessmentListIds } = useMemo(() => {
    const assessments =
      assessmentsData?.pages.reduce<Assessment[]>(
        (prev, curr) => [
          ...prev,
          ...curr.results.map((a) => ({
            ...a,
            methods: a.methods.filter((m) =>
              settings.methodType.length ? settings.methodType.includes(m.method_type) : true,
            ),
          })),
        ],
        [],
      ) ?? [];

    const assessmentListIds = assessments.reduce<string[]>((acc, assessment) => {
      const listId = assessment.methods.find(
        (method) => method.method_type === AssessmentMethodType.Grade,
      )?.select_list_id;

      return !listId || acc.includes(listId) ? acc : [...acc, listId];
    }, []);

    return { assessments, assessmentListIds };
  }, [assessmentsData, settings.methodType]);

  const initMarkbookParams: GetMarkbookParams = useMemo(() => {
    let filters: GetMarkbookParams['filters'] = {
      [FilterKeys.MethodTypes]: settings.methodType,
    };
    let arrangeBy: GetMarkbookParams['arrangeBy'] = MarkbookArrangeBy.Group;

    if (pageFilters) {
      const { area_of_learning, evaluation_type, ...markbookFilters } = pageFilters;
      filters = { ...filters, ...markbookFilters };
      arrangeBy =
        markbookFilters.age_group && markbookFilters.age_group.length > 0
          ? MarkbookArrangeBy.AgeGroup
          : MarkbookArrangeBy.Group;
    }

    return {
      schoolId: schoolId || '',
      pageSize: MARKBOOK_PAGE_SIZE,
      query: '',
      arrangeBy,
      filters,
      sort: DEFAULT_MARKBOOK_SORT,
    };
  }, [pageFilters, schoolId, settings.methodType]);

  const enableMarkbookQuery = Boolean(
    assessmentsData && !isAssessmentsLoading && pageFilters && schoolId,
  );

  const {
    setParams: setMarkbookParams,
    params: markbookParams,
    //loading is shown on markbook isFetching (not isLoading)
    //to pass the correct data to table on sorting or filter update
    isFetching: isMarkbookFetching,
  } = useGetMarkbookQuery(initMarkbookParams, {
    enabled: enableMarkbookQuery,
    refetchOnMount: 'always',
    onSettled: () => {
      setIsSorting(false);
    },
    onSuccess: (data) => {
      const params = data.map((data) => {
        const filters = removeObjectEmptyArrayValues(markbookParams.filters ?? {});
        const categoryParams = {
          ...markbookParams,
          pageNumber: INITIAL_PAGE_NUMBER,
          filters: {
            ...filters,
            ...(markbookParams.arrangeBy === MarkbookArrangeBy.AgeGroup
              ? { [FilterKeys.AgeGroup]: [data.category.id] }
              : { [FilterKeys.Group]: [data.category.id] }),
          },
        };

        queryClient.setQueryData([GET_MARKBOOK_QUERY, categoryParams], {
          pages: [[data]],
          pageParams: [INITIAL_PAGE_NUMBER],
        });

        return {
          id: data.category.id,
          params: { ...categoryParams, pageNumber: INITIAL_PAGE_NUMBER },
        };
      });

      setMarkbookQueriesParams(params);
    },
  });

  const { data: listsData, isLoading: isListsLoading } = useGetAssessmentsGradesForSchoolQuery(
    { schoolId: schoolId ?? '', listIds: assessmentListIds },
    {
      enabled: Boolean(schoolId && !isAssessmentsLoading && assessmentListIds.length),
    },
  );

  const lists = useMemo(
    () =>
      Object.values(listsData ?? {}).reduce<Record<string, AssessmentsGrade>>(
        (acc, list) => ({ ...acc, [list.id]: list }),
        {},
      ),
    [listsData],
  );

  const handleChangeSort = useCallback(
    ({ sortBy, assessmentId, subjectId }: HandleChangeSortParams) => {
      setIsSorting(true);

      const currentDirection = markbookParams.sort?.find((s) => s.sortBy === sortBy)?.direction;
      const newDirection =
        currentDirection === SORT_DIRECTION.ASC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC;

      switch (sortBy) {
        case GetMarkbookSortBy.LastName:
          setMarkbookParams((p) => ({
            ...p,
            sort: [
              { sortBy: GetMarkbookSortBy.LastName, direction: newDirection },
              { sortBy: GetMarkbookSortBy.GivenName, direction: newDirection },
            ],
          }));
          break;
        case GetMarkbookSortBy.Comment:
        case GetMarkbookSortBy.Grade:
        case GetMarkbookSortBy.Score:
          if (!assessmentId || !subjectId) return;
          setMarkbookParams((p) => ({
            ...p,
            sort: [
              { sortBy, direction: newDirection, assessmentId, subjectId },
              ...DEFAULT_MARKBOOK_SORT,
            ],
          }));
          break;
      }
    },
    [markbookParams.sort, setMarkbookParams],
  );

  const handleSetFiltersQuery = useCallback(
    (query: string) => {
      setMarkbookParams((p) => ({ ...p, query }));
    },
    [setMarkbookParams],
  );

  const {
    averageData,
    subjectAverageData,
    isAverageLoading,
    setBenchmarkEnabled,
    setBenchmarkAverage,
    benchmarkEnabled,
    setBenchmarkSubjectAverage,
  } = useAssessmentsAverage({
    canFetchAverage: Boolean(
      schoolId &&
        (settings.avgResults || settings.coloredCells) &&
        assessments.length &&
        markbookParams.filters,
    ),
    requestParams: {
      query: removeObjectUndefinedNullValues({
        assessment_ids: assessments.map((assessment) => assessment.id).join(','),
        group_ids: markbookParams.filters?.[FilterKeys.Group]?.length
          ? markbookParams.filters?.[FilterKeys.Group]?.join(',')
          : undefined,
        subject_ids: markbookParams.filters?.[FilterKeys.Subject]?.length
          ? markbookParams.filters?.[FilterKeys.Subject]?.join(',')
          : undefined,
        group_types: markbookParams.filters?.[FilterKeys.GroupTypes]?.length
          ? markbookParams.filters?.[FilterKeys.GroupTypes]?.join(',')
          : undefined,
        age_group_ids: markbookParams.filters?.[FilterKeys.AgeGroup]?.length
          ? markbookParams.filters?.[FilterKeys.AgeGroup]?.join(',')
          : undefined,
        date_from: markbookParams.filters?.[FilterKeys.Date]?.[0],
        method_types: markbookParams.filters?.[FilterKeys.MethodTypes]?.length
          ? markbookParams.filters?.[FilterKeys.MethodTypes]?.join(',')
          : undefined,
        date_to:
          markbookParams.filters?.[FilterKeys.Date]?.[1] ||
          markbookParams.filters?.[FilterKeys.Date]?.[0],
        sort_by: getSortParams([
          { columnTextId: 'assessment_name', direction: SORT_DIRECTION.ASC },
          { columnTextId: 'assessment_evaluation_type', direction: SORT_DIRECTION.ASC },
          { columnTextId: 'assessment_area_of_learning_name', direction: SORT_DIRECTION.ASC },
          { columnTextId: 'assessment_date', direction: SORT_DIRECTION.DESC },
        ]),
      }),

      params: { schoolId: schoolId ?? '' },
    },
    settings,
  });

  const handleSuccessfulEntryUpdate = useCallback(() => {
    queryClient.invalidateQueries([GET_ASSESSMENTS_AVERAGE_QUERY]);
  }, [queryClient]);

  const isLoadingApplicableLists = !!assessmentListIds.length && isListsLoading;
  const isLoadingFullMarkbook = isMarkbookFetching && !isSorting;
  const isLoading = isAssessmentsLoading || isLoadingFullMarkbook || isLoadingApplicableLists;
  const isMarkbookSorting = isMarkbookFetching && isSorting;

  useEffect(() => {
    setAssessmentsParams((p) => ({
      ...p,
      filters: { ...p.filters, [FilterKeys.Assessment]: pageFilters?.[FilterKeys.Assessment] },
    }));
  }, [pageFilters, setAssessmentsParams]);

  useEffect(() => {
    setMarkbookParams(initMarkbookParams);
    setIsSorting(false);
  }, [initMarkbookParams, setMarkbookParams]);

  return {
    assessments,
    lists,
    isLoading,
    isSorting: isMarkbookSorting,
    isAverageLoading,
    isListsLoading: isLoadingApplicableLists,
    averageData,
    subjectAverageData,
    markbookQueriesParams,
    markbookParams,
    handleChangeSort,
    handleChangeQuery: handleSetFiltersQuery,
    handleSuccessfulEntryUpdate,
    setBenchmarkEnabled,
    setBenchmarkAverage,
    setBenchmarkSubjectAverage,
    benchmarkEnabled,
  };
};
